import { __rest } from "tslib";
import React, { useEffect, useRef, useState } from 'react';
import { StyledInput } from '../text-input';
import styled from '@emotion/styled';
import { useField } from 'formik';
import { theme } from '../../../theme';
import { getErrorStyles } from '../text-input/styles';
const MultiCharInputContainer = styled.div `
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	gap: 0.5rem;
	justify-content: space-between;
	align-items: center;
`;
const SingleCharInput = styled(StyledInput) `
	${theme.typography.IR130}

	min-width: 0;
	caret-color: transparent;

	line-height: 125%;
	flex-grow: 1;
	text-align: center;

	border: solid 1px;
	border-radius: ${theme.radii.radii30};
	border-color: ${theme.colors.LM_NU30};
	outline-offset: -2px;

	color: #11233a;
	background-color: ${theme.colors.LM_NU10};

	:hover {
		background-color: ${theme.colors.LM_NU20};
	}

	:active {
		outline: 2px solid ${theme.colors.BB100};
		background-color: ${theme.colors.LM_NU20};
	}
	:focus {
		outline: 2px solid ${theme.colors.BB80};
		background-color: ${theme.colors.LM_NU20};
	}

	${({ error, touched }) => (Object.assign({}, getErrorStyles(error, touched)))};

	// This removes the default increment/decrement arrows for a number input
	// https://stackoverflow.com/a/71297126
	::-webkit-outer-spin-button,
	::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
		-moz-appearance: textfield !important;
	}
`;
export const MultiCharInput = (_a) => {
    var { numChars = 6, name, type = 'text' } = _a, props = __rest(_a, ["numChars", "name", "type"]);
    const [multiCharValue, setMultiCharValue] = useState('');
    const inputElementsRef = useRef([]);
    // Formik connector
    const [, meta, helpers] = useField(name);
    const { touched, error } = meta;
    const { setTouched, setValue: setFormikValue } = helpers;
    useEffect(() => {
        var _a;
        if (multiCharValue.trim() === '')
            setMultiCharValue('');
        void setFormikValue(multiCharValue);
        (_a = inputElementsRef === null || inputElementsRef === void 0 ? void 0 : inputElementsRef.current[multiCharValue.length]) === null || _a === void 0 ? void 0 : _a.focus();
    }, [multiCharValue]);
    const handleChange = (e, idx) => {
        if (e.target.value === '')
            return;
        // allow overwrite of current character while keeping rest of value
        if (idx < multiCharValue.length) {
            const arr = multiCharValue.split('');
            const inputVal = e.target.value;
            const newChar = inputVal.substring(inputVal.length - 1);
            const parts = [
                arr.slice(0, idx),
                [newChar],
                arr.slice(idx + 1, arr.length),
            ];
            setMultiCharValue((_) => parts.flat(1).join(''));
            return;
        }
        // append the most recent inputted character to the value
        if (multiCharValue.length < numChars) {
            setMultiCharValue((prevVal) => `${prevVal}${e.target.value}`);
        }
    };
    // backspace deletes characters from right to left
    const handleKeyDown = (e) => {
        if (e.key === 'Backspace') {
            setMultiCharValue((prevVal) => prevVal.substring(0, prevVal.length - 1));
        }
    };
    return (React.createElement(MultiCharInputContainer, { "data-testid": "multi-char-input", style: props.style, className: props.className }, Array.from({ length: numChars }, (_, idx) => {
        var _a;
        return (React.createElement(SingleCharInput, { "data-testid": `input-${idx}`, type: type, ref: (input) => (inputElementsRef.current[idx] = input), key: idx, maxlength: "1", value: (_a = multiCharValue[idx]) !== null && _a !== void 0 ? _a : '', onChange: (e) => handleChange(e, idx), onKeyDown: (e) => handleKeyDown(e), onBlur: () => {
                void setTouched(true);
            }, touched: touched, error: error }));
    })));
};
//# sourceMappingURL=MultiCharInput.js.map